import { Link, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import { ProductData, ProductDataContactPricingListNode } from "../../apiHooks";
import { Flex, LabelValue } from "../../components";
import { Price } from "../../components/Price";
import { useFormatAgeRange, useNextTranslation } from "../../hooks";
import { usePriceFormatter } from "../../hooks/usePriceFormatter";
import { makeStyles } from "../../style/makeStyles";
import { Price as PriceType } from "../../types/Price";

const useStyles = makeStyles()((theme) => ({
    multiPricing: {
        lineHeight: 1.3,
        marginTop: theme.spacing(1),
    },
    multiPricingItem: {
        marginBottom: theme.spacing(1),
    },
    multiPricingLabel: {
        display: "flex",
        flexDirection: "column",
    },
    multiPricingLabelName: {
        fontSize: "0.7em",
    },
    multiPricingLabelAgeRange: {
        whiteSpace: "nowrap",
        fontSize: "0.6em",
        color: theme.palette.grey[500],
    },
}));

const PriceStyled = styled(Price)({
    fontSize: "1.4em",
    fontWeight: 500,
});

type ProductPricingData = {
    description: string;
    price: PriceType & { capacity: number | null };
};

function usePricingDescription(product: ProductData): ({ display: true } & ProductPricingData) | { display: false } {
    const description = product.contentList.nodes.find((x) => x.type === "CONTACT_PRICING_DESCRIPTION");
    const pricing = product.contactPricingList?.nodes[0];

    if (description && pricing && pricing.type !== "PER_GROUP") {
        return { display: true, description: description.description!, price: pricing.priceList.nodes[0] };
    }
    return { display: false };
}

type AvailabilityContactProps = {
    product: ProductData;
};

export const AvailabilityContact = ({ product }: AvailabilityContactProps) => {
    const [t] = useNextTranslation("product");
    const [tAvailability] = useNextTranslation("availability");

    const { supplier } = product;
    const { companyContactPhone, mainContactEmail, websiteUrl } = supplier ?? {};

    const contactDetail = { phone: companyContactPhone, emailAddress: mainContactEmail };

    const pricingDescription = usePricingDescription(product);

    return (
        <div>
            {pricingDescription.display ? <PricingDescription {...pricingDescription} /> : undefined}
            <Typography>{t("contactSupplier", contactDetail)}</Typography>

            <PricingMetaData>
                {websiteUrl ? (
                    <LabelValue label={tAvailability("label.website")}>
                        <Link
                            rel="noreferrer nofollow noopener"
                            target={"_blank"}
                            color="primary"
                            href={enforceSchemaForUrl(websiteUrl)}
                        >
                            {websiteUrl}
                        </Link>
                    </LabelValue>
                ) : undefined}
                {pricingDescription.display ? undefined : <AvailabilityContactPricing product={product} />}
            </PricingMetaData>
        </div>
    );
};

const PricingMetaData = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    "&:empty": {
        display: "none",
    },
}));

function PricingDescription({ description, price }: ProductPricingData) {
    return (
        <PricingWithContentReplacement
            content={description}
            price={price.amount}
            capacity={price.capacity}
            currency={price.currency}
        />
    );
}

function PricingWithContentReplacement({
    content,
    price,
    capacity,
    currency,
}: {
    content: string | null;
    price: number;
    capacity: number | null;
    currency: string;
}) {
    const priceFormatter = usePriceFormatter(currency);
    const [t] = useNextTranslation("availability");

    const placeholders = {
        price: priceFormatter(price),
        guestsCapacity: t("price.maxCapacity", { count: capacity ?? 0 }),
    };
    return (
        <div>
            <PlaceholderReplacement html={content ?? ""} placeholders={placeholders} />
        </div>
    );
}

const HAS_SCHEMA_REGEXP = /^https?:\/\//;

function enforceSchemaForUrl(url: string) {
    if (HAS_SCHEMA_REGEXP.test(url)) {
        return url;
    }
    return `http://${url}`;
}

const AvailabilityContactPricing = ({ product }: { product: ProductData }) => {
    if (!product.contactPricingList?.nodes.length) {
        return null;
    }
    return (
        <div>
            {product.contactPricingList.nodes.map(({ priceList }, i: number) => {
                return <AvailabilityContactPricingPerPerson priceList={priceList} key={i} />;
            })}
        </div>
    );
};

interface AvailabilityContactPricingPerPersonProps {
    priceList: ProductDataContactPricingListNode["priceList"];
}

const PricePerPersonStyled = styled(PriceStyled)({
    justifySelf: "end",
});
const AvailabilityContactPricingPerPerson = ({ priceList }: AvailabilityContactPricingPerPersonProps) => {
    const { classes } = useStyles();
    const formatAgeRange = useFormatAgeRange();
    return (
        <div className={classes.multiPricing}>
            {priceList.nodes.map((x) => {
                return (
                    <Flex middle spaceBetween key={x.name + x.currency + x.amount} className={classes.multiPricingItem}>
                        <Typography className={classes.multiPricingLabel}>
                            <span className={classes.multiPricingLabelName}>{x.name}</span>
                            <span className={classes.multiPricingLabelAgeRange}>
                                {formatAgeRange(x.minAge, x.maxAge)}
                            </span>
                        </Typography>
                        <PricePerPersonStyled amount={x.amount} currency={x.currency} />
                    </Flex>
                );
            })}
        </div>
    );
};

type Placeholders = Record<string, (() => string) | string>;
type PlaceholderReplacementProps = {
    html: string;
    placeholders: Placeholders;
};

function PlaceholderReplacement({ html, placeholders }: PlaceholderReplacementProps) {
    return <Typography component="div" dangerouslySetInnerHTML={{ __html: replacePlaceholders(html, placeholders) }} />;
}

const replacePlaceholders = (content: string, placeholders: Placeholders) => {
    return content.replace(/\{\s*([a-z0-9]+)\s*\}/gi, (matchString, placeholder) => {
        if (Object.prototype.hasOwnProperty.call(placeholders, placeholder)) {
            const placeholderContent = placeholders[placeholder];
            if (placeholderContent instanceof Function) {
                return placeholderContent();
            }
            return placeholderContent;
        }

        return matchString;
    });
};
